import { useState, useMemo } from 'react';
import { BackToTopPill } from "@/components/BackToTopPill";
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { StatCard } from '@/components/shared/StatCard';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/ui/status-badge';
import { consumableTransactionStyle } from '@/features/consumables/utils/status-style';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Beaker,
  Plus,
  Minus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Loader2,
  History,
  DollarSign,
  Package,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import {
  useConsumableTypes,
  useConsumableTransactions,
  useConsumableMutations,
} from '@/features/consumables/hooks/useConsumables';
import { useLocation } from '@/contexts/LocationContext';
import { useLocations } from '@/features/locations/hooks/useLocations';
import ConsumableTypeModal from '@/features/consumables/components/ConsumableTypeModal';
import StockAdjustModal from '@/features/consumables/components/StockAdjustModal';
import type { ConsumableType } from '@/features/consumables/types';
import { Skeleton } from '@/components/ui/skeleton';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar } from '@/components/layout/PageActionBar';

function getStockStatus(type: ConsumableType) {
  if (type.stock_on_hand <= 0) return 'out';
  if (
    type.low_stock_threshold != null &&
    type.stock_on_hand <= type.low_stock_threshold
  )
    return 'low';
  return 'ok';
}

function StockStatusBadge({ type }: { type: ConsumableType }) {
  const status = getStockStatus(type);
  if (status === 'out')
    return <Badge variant="destructive">Out of Stock</Badge>;
  if (status === 'low')
    return (
      <Badge variant="outline" className="border-amber-400 text-amber-700">
        Low Stock
      </Badge>
    );
  return null;
}

// ============================================================================
// SUMMARY CARDS
// ============================================================================

function formatCurrency(value: number): string {
  return value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

function SummaryCards({
  consumableTypes,
  isLoading,
  lowStockFilterActive,
  onLowStockClick,
}: {
  consumableTypes: ConsumableType[];
  isLoading: boolean;
  lowStockFilterActive?: boolean;
  onLowStockClick?: () => void;
}) {
  const stats = useMemo(() => {
    const totalValue = consumableTypes.reduce(
      (sum, t) => sum + (t.unit_cost != null ? t.unit_cost * t.stock_on_hand : 0),
      0
    );
    const totalItems = consumableTypes.length;
    const lowStockCount = consumableTypes.filter(
      (t) => getStockStatus(t) === 'low' || getStockStatus(t) === 'out'
    ).length;
    return { totalValue, totalItems, lowStockCount };
  }, [consumableTypes]);

  if (isLoading) {
    return (
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-7 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const lowStockAccent = stats.lowStockCount > 0 || lowStockFilterActive;

  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
      <StatCard
        icon={DollarSign}
        label="Total Inventory Value"
        value={formatCurrency(stats.totalValue)}
      />
      <StatCard icon={Package} label="Consumable Types" value={stats.totalItems} />
      <StatCard
        icon={AlertTriangle}
        accent={lowStockAccent ? 'warning' : 'neutral'}
        onClick={onLowStockClick}
        className={cn(
          'transition-colors',
          onLowStockClick && 'cursor-pointer hover:border-amber-300',
          lowStockFilterActive && 'border-amber-400 ring-1 ring-amber-400',
        )}
        label={
          <>
            Low / Out of Stock
            {lowStockFilterActive && (
              <span className="ml-1 text-muted-foreground">(filtered)</span>
            )}
          </>
        }
        value={stats.lowStockCount}
      />
    </div>
  );
}

// ============================================================================
// INVENTORY TAB
// ============================================================================

type SortField = 'stock_on_hand' | 'total_value';
type SortDir = 'asc' | 'desc';

function SortIcon({ field, activeField, activeDir }: { field: SortField; activeField: SortField | null; activeDir: SortDir }) {
  if (activeField !== field) return <ArrowUpDown className="h-3.5 w-3.5 ml-1 text-muted-foreground/50" />;
  return activeDir === 'asc'
    ? <ArrowUp className="h-3.5 w-3.5 ml-1" />
    : <ArrowDown className="h-3.5 w-3.5 ml-1" />;
}

function InventoryTab({
  consumableTypes,
  isLoading,
  hasLocationSelected,
  lowStockFilter,
  hideZeroStock,
  onToggleHideZeroStock,
  onOpenTypeModal,
  onAddStock,
  onConsumeStock,
  onDeleteType,
}: {
  consumableTypes: ConsumableType[];
  isLoading: boolean;
  hasLocationSelected: boolean;
  lowStockFilter: boolean;
  hideZeroStock: boolean;
  onToggleHideZeroStock: () => void;
  onOpenTypeModal: (type?: ConsumableType) => void;
  onAddStock: (type: ConsumableType) => void;
  onConsumeStock: (type: ConsumableType) => void;
  onDeleteType: (id: string) => void;
}) {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedTypes = useMemo(() => {
    let items = [...consumableTypes];

    // Filter
    if (lowStockFilter) {
      items = items.filter((t) => {
        const s = getStockStatus(t);
        return s === 'low' || s === 'out';
      });
    }
    if (hideZeroStock && hasLocationSelected) {
      items = items.filter((t) => t.stock_on_hand > 0);
    }

    // Sort
    if (sortField) {
      items.sort((a, b) => {
        let aVal: number;
        let bVal: number;
        if (sortField === 'stock_on_hand') {
          aVal = a.stock_on_hand;
          bVal = b.stock_on_hand;
        } else {
          aVal = a.unit_cost != null ? a.unit_cost * a.stock_on_hand : 0;
          bVal = b.unit_cost != null ? b.unit_cost * b.stock_on_hand : 0;
        }
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }

    return items;
  }, [consumableTypes, sortField, sortDir, lowStockFilter, hideZeroStock, hasLocationSelected]);

  const hiddenCount = hasLocationSelected && hideZeroStock
    ? consumableTypes.filter((t) => t.stock_on_hand <= 0).length
    : 0;

  const colCount = 7;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>
                <button
                  className="inline-flex items-center hover:text-foreground transition-colors"
                  onClick={() => toggleSort('stock_on_hand')}
                >
                  Stock on Hand
                  <SortIcon field="stock_on_hand" activeField={sortField} activeDir={sortDir} />
                </button>
              </TableHead>
              <TableHead>Unit Cost</TableHead>
              <TableHead>
                <button
                  className="inline-flex items-center hover:text-foreground transition-colors"
                  onClick={() => toggleSort('total_value')}
                >
                  Total Value
                  <SortIcon field="total_value" activeField={sortField} activeDir={sortDir} />
                </button>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right w-[180px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} className="h-24">
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-8 w-full" />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            )}

            {!isLoading && sortedTypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} className="p-0">
                  {lowStockFilter ? (
                    <EmptyState
                      size="sm"
                      icon={Beaker}
                      title="No low or out of stock items"
                      description="All consumables are adequately stocked."
                    />
                  ) : (
                    <EmptyState
                      size="sm"
                      icon={Beaker}
                      title="No consumables yet"
                      description="Add your first consumable to start tracking inventory."
                      action={
                        <Button variant="outline" size="sm" onClick={() => onOpenTypeModal()}>
                          <Plus className="h-4 w-4 mr-1" />
                          Add Consumable
                        </Button>
                      }
                    />
                  )}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              sortedTypes.map((type) => {
                const totalValue = type.unit_cost != null ? type.unit_cost * type.stock_on_hand : null;
                return (
                  <TableRow key={type.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{type.name}</span>
                        {!type.is_active && (
                          <Badge variant="secondary" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {type.category ? (
                        <Badge variant="outline">{type.category}</Badge>
                      ) : (
                        <span className="text-muted-foreground">--</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium tabular-nums">
                      {type.stock_on_hand} {type.unit}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {type.unit_cost != null
                        ? formatCurrency(type.unit_cost)
                        : '--'}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {totalValue != null ? formatCurrency(totalValue) : '--'}
                    </TableCell>
                    <TableCell>
                      <StockStatusBadge type={type} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {hasLocationSelected ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => onAddStock(type)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => onConsumeStock(type)}
                            >
                              <Minus className="h-3 w-3 mr-1" />
                              Use
                            </Button>
                          </>
                        ) : (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    disabled
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                Select a location to adjust stock
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    disabled
                                  >
                                    <Minus className="h-3 w-3 mr-1" />
                                    Use
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                Select a location to adjust stock
                              </TooltipContent>
                            </Tooltip>
                          </>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => onOpenTypeModal(type)}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit Type
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onDeleteType(type.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Type
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </CardContent>
      {hasLocationSelected && hiddenCount > 0 && (
        <div className="border-t px-4 py-2.5 flex items-center justify-between text-sm text-muted-foreground">
          <span>{hiddenCount} item{hiddenCount !== 1 ? 's' : ''} with zero stock hidden</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={onToggleHideZeroStock}
          >
            Show all
          </Button>
        </div>
      )}
      {hasLocationSelected && !hideZeroStock && consumableTypes.some((t) => t.stock_on_hand <= 0) && (
        <div className="border-t px-4 py-2.5 flex items-center justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            onClick={onToggleHideZeroStock}
          >
            Hide zero-stock items
          </Button>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// ACTIVITY LOG TAB
// ============================================================================

function TransactionTypeBadge({
  type,
}: {
  type: 'add' | 'consume' | 'adjust';
}) {
  return <StatusBadge status={consumableTransactionStyle(type)} />;
}

function ActivityLogTab({
  consumableTypes,
}: {
  consumableTypes: ConsumableType[];
}) {
  const [filterTypeId, setFilterTypeId] = useState<string>('all');
  const { selectedLocation } = useLocation();
  const { locations } = useLocations();
  const showLocationColumn = !selectedLocation;

  const transactionsQuery = useConsumableTransactions(
    filterTypeId !== 'all' ? filterTypeId : undefined
  );
  const transactions = transactionsQuery.data ?? [];

  // Build lookup maps
  const typeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of consumableTypes) {
      map.set(t.id, t.name);
    }
    return map;
  }, [consumableTypes]);

  const locationNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locations) {
      map.set(l.id, l.name);
    }
    return map;
  }, [locations]);

  const colSpan = showLocationColumn ? 8 : 7;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Filter by Item</label>
          <Select value={filterTypeId} onValueChange={setFilterTypeId}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="All items" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All items</SelectItem>
              {consumableTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Item</TableHead>
                {showLocationColumn && <TableHead>Location</TableHead>}
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Before</TableHead>
                <TableHead className="text-right">After</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactionsQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={colSpan} className="h-24">
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {!transactionsQuery.isLoading && transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colSpan} className="p-0">
                    <EmptyState
                      size="sm"
                      icon={History}
                      title="No transactions yet"
                      description="Add or consume stock to see activity here."
                    />
                  </TableCell>
                </TableRow>
              )}

              {!transactionsQuery.isLoading &&
                transactions.map((txn) => {
                  const displayQty =
                    txn.transaction_type === 'consume'
                      ? `-${txn.quantity}`
                      : txn.transaction_type === 'add'
                        ? `+${txn.quantity}`
                        : String(txn.quantity);

                  return (
                    <TableRow key={txn.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {new Date(txn.created_at).toLocaleDateString(
                          undefined,
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          }
                        )}
                        <span className="text-muted-foreground ml-1.5">
                          {new Date(txn.created_at).toLocaleTimeString(
                            undefined,
                            {
                              hour: '2-digit',
                              minute: '2-digit',
                            }
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">
                        {typeNameMap.get(txn.consumable_type_id) ?? 'Unknown'}
                      </TableCell>
                      {showLocationColumn && (
                        <TableCell className="text-sm">
                          {txn.location_id
                            ? locationNameMap.get(txn.location_id) ?? 'Unknown'
                            : <span className="text-muted-foreground">--</span>}
                        </TableCell>
                      )}
                      <TableCell>
                        <TransactionTypeBadge type={txn.transaction_type} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {displayQty}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {txn.stock_before}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {txn.stock_after}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                        {txn.notes || '--'}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

function ConsumablesContent() {
  const { t } = useTranslation('inventory');
  // Modals
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<ConsumableType | null>(null);
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [stockModalMode, setStockModalMode] = useState<'add' | 'consume'>(
    'add'
  );
  const [stockModalType, setStockModalType] = useState<ConsumableType | null>(
    null
  );
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [lowStockFilter, setLowStockFilter] = useState(false);
  const [hideZeroStock, setHideZeroStock] = useState(true);

  const typesQuery = useConsumableTypes();
  const { deleteType, hasLocationSelected } = useConsumableMutations();

  const consumableTypes = typesQuery.data ?? [];
  const isLoading = typesQuery.isLoading;

  usePageTitle(t('consumables.title'), t('consumables.subtitle'));

  const handleOpenTypeModal = (type?: ConsumableType) => {
    setEditingType(type ?? null);
    setTypeModalOpen(true);
  };

  const handleAddStock = (type: ConsumableType) => {
    setStockModalType(type);
    setStockModalMode('add');
    setStockModalOpen(true);
  };

  const handleConsumeStock = (type: ConsumableType) => {
    setStockModalType(type);
    setStockModalMode('consume');
    setStockModalOpen(true);
  };

  const handleDeleteType = () => {
    if (deleteConfirmId) {
      deleteType.mutate(deleteConfirmId, {
        onSettled: () => setDeleteConfirmId(null),
      });
    }
  };

  return (
    <div className="flex flex-col">
      <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6">
        <Tabs defaultValue="inventory">
          <PageActionBar
            ariaLabel="Consumables actions"
            left={
              <TabsList className="h-8">
                <TabsTrigger value="inventory" className="h-6 text-xs">{t('consumables.inventory')}</TabsTrigger>
                <TabsTrigger value="activity" className="h-6 text-xs">{t('consumables.activityLog')}</TabsTrigger>
              </TabsList>
            }
            right={
              <Button size="sm" className="h-8 px-3 text-xs gap-1.5" onClick={() => handleOpenTypeModal()}>
                <Plus className="h-3.5 w-3.5" />
                {t('consumables.addConsumable')}
              </Button>
            }
          />

          <div className="mt-6 space-y-6">
            {/* Summary Cards */}
            <SummaryCards
              consumableTypes={consumableTypes}
              isLoading={isLoading}
              lowStockFilterActive={lowStockFilter}
              onLowStockClick={() => setLowStockFilter((f) => !f)}
            />

            <TabsContent value="inventory" className="mt-0">
              <InventoryTab
                consumableTypes={consumableTypes}
                isLoading={isLoading}
                hasLocationSelected={hasLocationSelected}
                lowStockFilter={lowStockFilter}
                hideZeroStock={hideZeroStock}
                onToggleHideZeroStock={() => setHideZeroStock((v) => !v)}
                onOpenTypeModal={handleOpenTypeModal}
                onAddStock={handleAddStock}
                onConsumeStock={handleConsumeStock}
                onDeleteType={setDeleteConfirmId}
              />
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              <ActivityLogTab consumableTypes={consumableTypes} />
            </TabsContent>
          </div>
        </Tabs>
      </main>

      {/* Modals */}
      <ConsumableTypeModal
        open={typeModalOpen}
        onOpenChange={(val) => {
          setTypeModalOpen(val);
          if (!val) setEditingType(null);
        }}
        consumableType={editingType}
      />

      {stockModalType && (
        <StockAdjustModal
          open={stockModalOpen}
          onOpenChange={(val) => {
            setStockModalOpen(val);
            if (!val) setStockModalType(null);
          }}
          consumableType={stockModalType}
          mode={stockModalMode}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => !open && setDeleteConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('consumables.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('consumables.deleteDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteType}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteType.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <BackToTopPill />
    </div>
  );
}

// LG-1 (post-PR-528 audit): page-level RoleGuard removed. Sidebar shows
// /consumables to anyone with `consumables.view` (which includes Viewer),
// so the RoleGuard's allowedRoles list contradicted the sidebar — Viewer
// landed on the route then saw the "denied" page. Per-action gates inside
// ConsumablesContent already block writes for read-only roles; mirroring
// the /orders, /products, /inventory pattern (no page-level RoleGuard,
// backend permission checks are the source of truth).
const Consumables = () => <ConsumablesContent />;

export default Consumables;
